<template>
  <div class="w-full h-full bg-white flex flex-col px-8 overflow-y-auto">
    <div class="flex items-center justify-between py-8">
      <h2 class="font-semibold">Vector Collections</h2>
      <busy-button id="newCollectionButton" variant="bordered" class="w-48" @click="startCreate">
        New collection
      </busy-button>
    </div>

    <!-- vector geometry is SSOT-locked (must match what each collection was ingested with) -->
    <div class="flex gap-10 text-gray-dark mb-6">
      <div class="flex flex-col gap-1">
        <label class="font-bold">Vector size (locked)</label>
        <span>{{ vectorSize || '—' }}</span>
      </div>
      <div class="flex flex-col gap-1">
        <label class="font-bold">Distance (locked)</label>
        <span>{{ distance || '—' }}</span>
      </div>
    </div>

    <table class="w-full text-sm mb-8">
      <thead>
        <tr class="text-left text-gray-dark border-solid border-0 border-b border-gray-border">
          <th class="py-2">Name</th>
          <th class="py-2">Source type</th>
          <th class="py-2">Assignment</th>
          <th class="py-2">Indexed fields</th>
          <th class="py-2">Quantized</th>
          <th class="py-2"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="c in collections" :key="c.id" class="border-solid border-0 border-b border-gray-border">
          <td class="py-2 font-mono">{{ c.name }}</td>
          <td class="py-2">{{ c.source_type }}</td>
          <td class="py-2">{{ c.organization_id || 'Shared (all orgs)' }}</td>
          <td class="py-2">{{ (c.payload_indexes || []).map(p => p.field).join(', ') || '—' }}</td>
          <td class="py-2">{{ c.quantization ? 'yes' : 'default' }}</td>
          <td class="py-2 text-right">
            <a class="text-primary cursor-pointer mr-4" @click="startEdit(c)">Edit</a>
            <a class="text-red-600 cursor-pointer" @click="remove(c)">Delete</a>
          </td>
        </tr>
        <tr v-if="!collections.length">
          <td colspan="6" class="py-4 text-gray">No collections yet.</td>
        </tr>
      </tbody>
    </table>

    <div v-if="isEditorOpen" class="border-solid border border-gray-border rounded p-5 mb-10">
      <h3 class="font-semibold mb-4">{{ editingId ? 'Edit collection' : 'New collection' }}</h3>

      <div class="flex gap-4">
        <div class="flex flex-col gap-1 flex-1">
          <label class="font-medium">Collection name</label>
          <base-input v-model.trim="form.name" name="vc-name" variant="editor" class="border-gray-border h-10" placeholder="e.g. products"/>
        </div>
        <div class="flex flex-col gap-1 flex-1">
          <label class="font-medium">Source type</label>
          <base-select :value="form.sourceType" :options="sourceTypes" class="h-10" @input="form.sourceType = $event"/>
        </div>
        <div class="flex flex-col gap-1 flex-1">
          <label class="font-medium">Organization (blank = shared)</label>
          <base-input v-model.trim="form.organizationId" name="vc-org" variant="editor" class="border-gray-border h-10" placeholder="org id"/>
        </div>
      </div>

      <div class="mt-5">
        <div class="flex items-center justify-between">
          <label class="font-medium">Indexed payload fields</label>
          <a class="text-primary cursor-pointer text-sm" @click="addPayloadIndex">+ Add field</a>
        </div>
        <div v-for="(row, i) in form.payloadIndexes" :key="i" class="flex gap-3 items-center mt-2">
          <base-input v-model.trim="row.field" :name="`vc-field-${i}`" variant="editor" class="border-gray-border h-9 flex-1" placeholder="payload field"/>
          <base-select :value="row.schema" :options="schemaTypes" class="h-9 w-40" @input="row.schema = $event"/>
          <a class="text-red-600 cursor-pointer text-sm" @click="removePayloadIndex(i)">remove</a>
        </div>
        <p v-if="!form.payloadIndexes.length" class="text-gray text-sm mt-1">
          tenant_id is always indexed automatically. Add fields you want to filter/facet on.
        </p>
      </div>

      <label class="flex items-center gap-2 mt-5">
        <input type="checkbox" v-model="form.quantize"/>
        <span>Scalar int8 quantization (smaller, faster; tiny recall cost)</span>
      </label>

      <div class="flex gap-4 mt-6">
        <busy-button id="saveCollectionButton" variant="primary" class="w-40" :is-loading="isSaving" :is-disabled="!isFormValid || isSaving" @click="save">
          Save
        </busy-button>
        <busy-button id="cancelCollectionButton" variant="bordered" class="w-40" :is-disabled="isSaving" @click="resetForm">
          Cancel
        </busy-button>
      </div>
    </div>
  </div>
</template>

<script>
import vectorCollectionService from '@/services/amio/vector-collection.service'
import BusyButton from '@/components/common/BusyButton'
import toaster from '@/utils/toaster'

const SOURCE_TYPES = ['products', 'documents', 'answers']
const FALLBACK_SCHEMA_TYPES = ['keyword', 'text', 'float', 'bool']
// matches the indexer provisioner's default quantization
const SCALAR_QUANTIZATION = { type: 'scalar', quantile: 0.99, alwaysRam: true }

function emptyForm() {
  return { name: '', sourceType: 'products', organizationId: '', payloadIndexes: [], quantize: false }
}

export default {
  name: 'SettingsVectorCollections',
  components: { BusyButton },

  data() {
    return {
      collections: [],
      schemaTypes: [...FALLBACK_SCHEMA_TYPES],
      vectorSize: null,
      distance: null,
      sourceTypes: SOURCE_TYPES,
      editingId: null,
      isEditorOpen: false,
      isSaving: false,
      form: emptyForm()
    }
  },

  computed: {
    isFormValid() {
      return Boolean(this.form.name) && Boolean(this.form.sourceType)
    }
  },

  methods: {
    async loadData() {
      const [collections, schema] = await Promise.all([
        vectorCollectionService.list(),
        vectorCollectionService.getIndexSchema()
      ])
      this.collections = collections
      if (schema.schema_types?.length) this.schemaTypes = schema.schema_types
      this.vectorSize = schema.vector_size
      this.distance = schema.distance
    },

    startCreate() {
      this.resetForm()
      this.isEditorOpen = true
    },

    startEdit(collection) {
      this.editingId = collection.id
      this.form = {
        name: collection.name,
        sourceType: collection.source_type,
        organizationId: collection.organization_id || '',
        payloadIndexes: (collection.payload_indexes || []).map(p => ({ ...p })),
        quantize: Boolean(collection.quantization)
      }
      this.isEditorOpen = true
    },

    resetForm() {
      this.editingId = null
      this.isEditorOpen = false
      this.form = emptyForm()
    },

    addPayloadIndex() {
      this.form.payloadIndexes.push({ field: '', schema: this.schemaTypes[0] || 'keyword' })
    },

    removePayloadIndex(index) {
      this.form.payloadIndexes.splice(index, 1)
    },

    buildPayload() {
      return {
        name: this.form.name,
        source_type: this.form.sourceType,
        organization_id: this.form.organizationId ? this.form.organizationId : null,
        // drop incomplete rows — the engine rejects payload indexes without a field name
        payload_indexes: this.form.payloadIndexes.filter(row => row.field && row.field.trim()),
        quantization: this.form.quantize ? { ...SCALAR_QUANTIZATION } : null
      }
    },

    async save() {
      this.isSaving = true
      try {
        const payload = this.buildPayload()
        if (this.editingId) {
          await vectorCollectionService.update(this.editingId, payload)
        } else {
          await vectorCollectionService.create(payload)
        }
        await this.loadData()
        this.resetForm()
      } catch (err) {
        toaster.error(err.message || err)
      } finally {
        this.isSaving = false
      }
    },

    async remove(collection) {
      // deleting a collection drops every point in it — make the one-click action deliberate
      if (!window.confirm(`Delete vector collection "${collection.name}"? This removes all its indexed documents.`)) return
      try {
        await vectorCollectionService.delete(collection.id)
        await this.loadData()
      } catch (err) {
        toaster.error(err.message || err)
      }
    }
  },

  async mounted() {
    try {
      await this.loadData()
    } catch (err) {
      toaster.error(err.message || err)
    }
  }
}
</script>
